import React from 'react';

import config from 'config';
import Head from 'next/head';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { defineMessages, useIntl } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import generateCdnImageUrl from '../../../src/utils/generateCdnImageUrl';
import { countryMessages } from '../../utils/countries';
import {
  getDomainCurrency,
  isSoldOut as getIsSoldOut,
} from '../../utils/selectors';
import { Component as ProductDetailPageSchema } from '../ProductDetailPageSchema';

const defaults = defineMessages({
  title: {
    id: 'site_seo_title.title_tag_offer',
    defaultMessage: '| SAVAGE X FENTY',
  },
  uktitle: {
    id: 'site_seo_title.title_tag_offer_uk',
    defaultMessage: '| SAVAGE X FENTY UK',
  },
});

const defaultCountries = config.get('public.domain.defaultCountries');
const setId = config.get('public.productTypes.setId');

const ProductHead = ({ product, ssrOriginPathname }) => {
  const reviews = useSelector(state => state.reviews);
  const { tld } = useDomain();
  const countryCode = defaultCountries[tld];
  const { formatMessage, formatNumber } = useIntl();
  const {
    default_unit_price: defaultUnitPrice,
    item_number: itemNumber,
    label,
    long_description: description,
    product_type_id: productTypeId,
    promos,
  } = product;
  const metaData = (product.meta_data && product.meta_data[0]) || {};
  const colorTags = product.bundle_color_tag_list || [];

  let title = metaData.meta_title_override;

  if (!title) {
    const colorSubtitle = colorTags.length ? `in ${colorTags.join(' & ')}` : '';
    title = `${product.label} ${colorSubtitle} ${formatMessage(
      countryCode === 'GB' ? defaults.uktitle : defaults.title
    )}`;
    if (countryCode !== 'US' && countryCode in countryMessages) {
      title += ` ${formatMessage(countryMessages[countryCode])}`;
    }
  }

  const price = formatNumber(product.retail_unit_price, {
    minimumFractionDigits: 2,
  });

  // offers
  const currency = useSelector(getDomainCurrency);
  const isSoldOut = useSelector(state => getIsSoldOut(state, { product }));
  const lowestPriceAvailable =
    formatNumber(
      promos.promo_price && promos.display_on_pdp
        ? promos.promo_price
        : defaultUnitPrice,
      {
        minimumFractionDigits: 2,
      }
    ) || price;

  // aggregateRating
  let bestRating;
  let reviewCount;
  let ratingValue;

  const isSet = productTypeId === setId;

  if (!isSet) {
    bestRating =
      reviews.reviews && reviews.reviews.length > 0
        ? reviews.reviews[0].overallRating
        : 0;
    ratingValue = reviews.summary.find(
      review => review.label === 'Overall'
    ).average;
    reviewCount = reviews.counts ? reviews.counts.reviewCount : 0;
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} key="metadescription" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={title} />
        <meta
          property="og:image"
          content={generateCdnImageUrl({
            product,
            angle: 'LAYDOWN',
            imageSize: 8,
          })}
        />
        <meta property="og:price:amount" content={price} />
        <meta property="og:price:currency" content={currency} />
        <ProductDetailPageSchema product={product} />
      </Head>
      <div>
        <div itemType="http://schema.org/Product" itemScope>
          <meta itemProp="name" content={label} />
          <link
            itemProp="image"
            href={generateCdnImageUrl({
              product,
              angle: 'LAYDOWN',
              imageSize: 8,
            })}
          />
          <meta itemProp="description" content={description} />
          <div itemProp="offers" itemType="http://schema.org/Offer" itemScope>
            <link
              itemProp="url"
              href={
                ssrOriginPathname ||
                window.location.origin + window.location.pathname
              }
            />
            <meta
              itemProp="availability"
              content={
                'https://schema.org/' + (isSoldOut ? 'OutOfStock' : 'InStock')
              }
            />
            <meta itemProp="priceCurrency" content={currency} />
            <meta
              itemProp="itemCondition"
              content="https://schema.org/NewCondition"
            />
            <meta itemProp="price" content={lowestPriceAvailable} />
          </div>
          {!isSet ? (
            <div
              itemProp="aggregateRating"
              itemType="http://schema.org/AggregateRating"
              itemScope
            >
              <meta itemProp="reviewCount" content={reviewCount} />
              <meta itemProp="ratingValue" content={ratingValue} />
              <meta itemProp="bestRating" content={bestRating} />
            </div>
          ) : null}
          <meta itemProp="sku" content={itemNumber} />
          <div itemProp="brand" itemType="http://schema.org/Brand" itemScope>
            <meta itemProp="name" content="Savage X Fenty" />
          </div>
        </div>
      </div>
    </>
  );
};

ProductHead.propTypes = {
  product: PropTypes.object.isRequired,
  ssrOriginPathname: PropTypes.string,
};

export default ProductHead;
